import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/UI/Badge';
import {
  type InAppNotificationOut,
  type Classification,
  useMarkReadMutation,
  useArchiveNotificationMutation,
  useUnarchiveNotificationMutation,
  useValidateActionMutation,
} from '@/redux/internal-services/notificationsApi';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const classificationBadge: Record<Classification, React.ComponentProps<typeof Badge>['variant']> =
  {
    critical: 'danger',
    important: 'warning',
    info: 'neutral',
  };

// ── Classification dot ────────────────────────────────────────────────────────

const ClassificationDot: React.FC<{ classification: Classification; unread: boolean }> = ({
  classification,
  unread,
}) => {
  const colorMap: Record<Classification, string> = {
    critical: 'bg-[#CF4148]',
    important: 'bg-yellow-400',
    info: 'bg-brand-dark',
  };
  return (
    <span
      className={cn(
        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
        unread ? colorMap[classification] : 'bg-[#EDEDED] dark:bg-white/10',
      )}
    />
  );
};

// ── Icon button ───────────────────────────────────────────────────────────────

const IconBtn: React.FC<{
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="flex h-9 w-9 items-center justify-center rounded-lg disabled:opacity-50 text-ui-text2 hover:bg-ui-hover hover:text-ui-text active:bg-ui-hover"
  >
    {children}
  </button>
);

// ── Props ─────────────────────────────────────────────────────────────────────

interface NotificationItemProps {
  notification: InAppNotificationOut;
  compact?: boolean;
  disableNavigation?: boolean;
  /** Called instead of navigating when the item has a pending action in a context where
   *  navigation is not desired (e.g. the workspace notifications panel).  */
  onAction?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, compact = false, disableNavigation = false, onAction }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [markRead] = useMarkReadMutation();
  const [archive, { isLoading: isArchiving }] = useArchiveNotificationMutation();
  const [unarchive, { isLoading: isUnarchiving }] = useUnarchiveNotificationMutation();
  const [validateAction, { isLoading: isValidating }] = useValidateActionMutation();

  const classification = notification.classification.toLowerCase() as Classification;

  const handleCompactClick = () => {
    if (notification.action_route && notification.is_action_required && notification.action_status === 'Pending') {
      if (onAction) {
        // Let parent handle in-workspace (e.g. WorkspaceNotificationsPanel opens approval inline)
        onAction();
      } else if (!disableNavigation) {
        const approvalMatch = notification.action_route.match(/^\/workflow\/approvals\/(.+)$/);
        const taskRouteMatch = notification.action_route.match(/^\/tasks\/(.+)$/);
        if (approvalMatch) {
          navigate(`/chat?approval=${approvalMatch[1]}`);
        } else if (taskRouteMatch) {
          // Tasks live inside the workspace — open via URL param so the task card expands
          navigate(`/chat?task=${taskRouteMatch[1]}`);
        } else {
          navigate(notification.action_route);
        }
      }
    } else if (!onAction && !disableNavigation && notification.source_module === 'TSK' && notification.source_entity_id) {
      // Non-action task notification (e.g. assignment, reminder) — open task in workspace
      navigate(`/chat?task=${notification.source_entity_id}`);
    }
    if (!notification.read) markRead(notification.id);
  };

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await archive(notification.id).unwrap();
    } catch {
      // 403 = system-critical; button is hidden for those, safe to swallow
    }
  };

  const handleUnarchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await unarchive(notification.id).unwrap();
    } catch {
      // swallow
    }
  };

  const handleTakeAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await validateAction(notification.id).unwrap();
      // Approvals open in workspace via /?approval=<id> instead of the removed standalone page
      const approvalMatch = result.action_route.match(/^\/workflow\/approvals\/(.+)$/);
      if (approvalMatch) {
        navigate(`/chat?approval=${approvalMatch[1]}`);
      } else {
        navigate(result.action_route);
      }
    } catch {
      // action expired or invalid — server updates status, RTK Query refetches
    }
  };

  const showActionButton =
    notification.is_action_required &&
    notification.action_status === 'Pending' &&
    notification.action_route;

  const actionExpired =
    notification.is_action_required &&
    (notification.action_status === 'Expired' || notification.action_status === 'Cancelled');

  // ── Compact (dropdown / workspace panel) ─────────────────────────────────

  if (compact) {
    const mod = notification.source_module?.toUpperCase();
    const hasPendingApproval = !!onAction && notification.is_action_required && notification.action_status === 'Pending' &&
      !!notification.action_route?.match(/^\/workflow\/approvals\//);
    const isTaskEntity = (mod === 'TSK' || mod === 'TASK') && !!notification.source_entity_id;
    const isMeetingEntity = (mod === 'MTG' || mod === 'MEETING') && !!notification.source_entity_id;
    const hasTaskAction = notification.is_action_required && notification.action_status === 'Pending' &&
      !!notification.action_route?.match(/^\/tasks\//);
    const engagementMatch = notification.action_route?.match(/^\/chat\?engagement=(.+)$/);
    const isEngagementEntity = !!engagementMatch && !isTaskEntity && !hasTaskAction && !isMeetingEntity;

    const handleEntityClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onAction) {
        onAction();
      } else if (!disableNavigation && notification.source_entity_id) {
        if (isTaskEntity || hasTaskAction) navigate(`/chat?task=${notification.source_entity_id}`);
        else if (isMeetingEntity) navigate(`/chat?meeting=${notification.source_entity_id}`);
        else if (isEngagementEntity && engagementMatch) navigate(`/chat?engagement=${engagementMatch[1]}`);
      }
      if (!notification.read) markRead(notification.id);
    };

    return (
      <div
        className="flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-ui-hover active:bg-ui-hover"
        onClick={handleCompactClick}
      >
        <ClassificationDot classification={classification} unread={!notification.read} />
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm leading-snug text-ui-text', !notification.read ? 'font-semibold' : 'font-medium')}>
            {notification.title}
          </p>
          <p className="mt-0.5 line-clamp-1 text-xs text-ui-text2">
            {notification.message}
          </p>
          {hasPendingApproval && (
            <p className="mt-1 text-[10px] font-semibold text-brand-dark">
              Tap to review →
            </p>
          )}
          {(isTaskEntity || hasTaskAction) && (
            <button onClick={handleEntityClick} className="mt-1 text-[10px] font-semibold text-brand-bright hover:underline">
              Open task →
            </button>
          )}
          {isMeetingEntity && (
            <button onClick={handleEntityClick} className="mt-1 text-[10px] font-semibold text-brand-bright hover:underline">
              Open meeting →
            </button>
          )}
          {isEngagementEntity && (
            <button onClick={handleEntityClick} className="mt-1 text-[10px] font-semibold text-brand-bright hover:underline">
              {t('notifications.openEngagement')}
            </button>
          )}
        </div>
        <span className="shrink-0 pt-0.5 text-xs text-ui-text3">
          {formatRelativeTime(notification.created_on)}
        </span>
      </div>
    );
  }

  // ── Full (notifications page) ─────────────────────────────────────────────

  return (
    <div
      onClick={() => { if (!notification.read) markRead(notification.id); }}
      className={cn(
        'group rounded-xl border p-4 transition-colors border-ui-border bg-ui-card hover:border-brand-light/60',
        !notification.read && 'cursor-pointer bg-brand-light/20 dark:bg-brand-dark/10',
      )}
    >
      <div className="flex gap-3">
        <ClassificationDot classification={classification} unread={!notification.read} />

        <div className="min-w-0 flex-1">
          {/* Title + time */}
          <div className="flex items-start justify-between gap-x-2">
            <p className={cn('text-sm leading-snug text-ui-text', !notification.read ? 'font-semibold' : 'font-medium')}>
              {notification.title}
            </p>
            <span className="shrink-0 pt-0.5 text-xs text-ui-text3">
              {formatRelativeTime(notification.created_on)}
            </span>
          </div>

          {/* Message — full text on the notifications page (read the whole message + sender) */}
          <p className="mt-1 whitespace-pre-line break-words text-xs text-ui-text2">
            {notification.message}
          </p>

          {/* Footer: badge + statuses + action + icon buttons */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant={classificationBadge[classification]}>
              {t(`notifications.classification.${classification}`)}
            </Badge>

            {notification.action_status === 'Completed' && (
              <span className="inline-flex items-center gap-1 text-xs text-green-500">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('notifications.actionCompleted')}
              </span>
            )}

            {actionExpired && (
              <span className="text-xs text-ui-text3">
                {t('notifications.actionExpired')}
              </span>
            )}

            {showActionButton && (
              <button
                type="button"
                onClick={handleTakeAction}
                disabled={isValidating}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-dark px-3 py-1.5 text-xs font-medium text-white hover:brightness-90 disabled:opacity-50 active:brightness-90"
              >
                {isValidating && (
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {t('notifications.takeAction')}
              </button>
            )}

            {/* Action icon buttons — always visible on mobile, hover-only on sm+ */}
            <div
              className="ml-auto flex items-center gap-0.5 opacity-40 transition-opacity group-hover:opacity-100"
            >
              {!notification.is_system_critical && (
                notification.archived ? (
                  <IconBtn
                    onClick={handleUnarchive}
                    disabled={isUnarchiving}
                    title={t('notifications.unarchive')}

                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-4 5l-2-2m0 0l-2 2m2-2v4" />
                    </svg>
                  </IconBtn>
                ) : (
                  <IconBtn
                    onClick={handleArchive}
                    disabled={isArchiving}
                    title={t('notifications.archive')}

                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </IconBtn>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
